import { TimecardClientNotesInputModes, TimecardInternalNotesInputModes } from "../../common/constants"
import { CreateJobRequest, CreateJobType, CreateJobTypes } from "./customerApi"
import { isValidJobName } from "../../domain/jobs/jobName"

export const CreateNewTypes = {
  Project: 'project',
  ServiceTicket: 'serviceTicket',
  MaintenanceContract: 'maintenanceContract',
  Consultation: 'consultation',
  Property: 'property',
} as const
export type CreateNewType = (typeof CreateNewTypes)[keyof typeof CreateNewTypes]

export const createNewTypeLabels: Record<CreateNewType, string> = {
  project: 'Project',
  serviceTicket: 'Service Ticket',
  maintenanceContract: 'Maintenance Contract',
  consultation: 'Consultation',
  property: 'Property',
}

// Returns the /{type}/create route segment for the job types, or null for
// consultation/property which are handled separately.
export const jobRouteSegment = (type: CreateNewType): CreateJobType | null => {
  switch (type) {
    case CreateNewTypes.Project:
      return CreateJobTypes.project
    case CreateNewTypes.ServiceTicket:
      return CreateJobTypes.serviceTicket
    case CreateNewTypes.MaintenanceContract:
      return CreateJobTypes.maintenanceContract
    default:
      return null
  }
}

export const getTimecardModes = (type: CreateNewType): { timecardInternalNotesInputMode?: number; timecardClientNotesInputMode?: number } => {
  switch (type) {
    case CreateNewTypes.Project:
      return {
        timecardInternalNotesInputMode: TimecardInternalNotesInputModes.ShowButNotRequireInput,
        timecardClientNotesInputMode: TimecardClientNotesInputModes.ShowAndNotificationOffByDefault,
      }
    case CreateNewTypes.ServiceTicket:
    case CreateNewTypes.MaintenanceContract:
      return {
        timecardInternalNotesInputMode: TimecardInternalNotesInputModes.ShowButNotRequireInput,
        timecardClientNotesInputMode: TimecardClientNotesInputModes.ShowAndNotificationOnByDefault,
      }
    default:
      return {}
  }
}

export const buildCreateJobRequest = (type: CreateNewType, customerId: number, propertyId: number | undefined, name: string): CreateJobRequest => ({
  customerId,
  propertyId,
  ...getTimecardModes(type),
  name: name.trim(),
})

export const jobRedirectUrl = (segment: CreateJobType, url: string): string => `#!/${segment}/${url}`

export const propertiesRedirectUrl = (customerUrl: string): string => `#!/customers/${customerUrl}/properties#new`

export const needsPropertySelection = (type: CreateNewType, propertyCount: number): boolean =>
  jobRouteSegment(type) !== null && propertyCount > 1

// Parses the #new* hash the legacy page auto-triggered on (customers.js:809-831).
export type NewHashAction = 'customer' | 'lead' | 'property' | 'consultation' | null

export const parseNewHashAction = (hash: string | null | undefined): NewHashAction => {
  if (!hash) {
    return null
  }
  if (hash.includes('newProperty')) {
    return 'property'
  }
  if (hash.includes('newConsultation')) {
    return 'consultation'
  }
  if (hash.includes('newCustomer')) {
    return 'customer'
  }
  if (hash.includes('newLead')) {
    return 'lead'
  }
  return null
}

export const needsCreateJobStep = (type: CreateNewType, propertyCount: number, nameRequired: boolean): boolean =>
  jobRouteSegment(type) !== null && (nameRequired || needsPropertySelection(type, propertyCount))

export const shouldShowPropertySelection = (pendingType: CreateNewType | null, propertyCount: number): boolean =>
  pendingType !== null && needsPropertySelection(pendingType, propertyCount)

export const createJobStepTitle = (pendingType: CreateNewType | null, nameRequired: boolean): string => {
  if (pendingType === null) {
    return 'Create New'
  }
  return nameRequired ? `New ${createNewTypeLabels[pendingType]}` : 'Select a property'
}

export type CanCreateJobParams = {
  nameRequired: boolean
  jobName: string
  showPropertySelection: boolean
  selectedPropertyId: number | undefined
}

export const canCreateJob = ({ nameRequired, jobName, showPropertySelection, selectedPropertyId }: CanCreateJobParams): boolean =>
  (!nameRequired || isValidJobName(jobName)) && (!showPropertySelection || selectedPropertyId !== undefined)
